import { Avatar, Box, Button, IconButton, MenuItem, Select, Stack, Typography } from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { Alarm, ArrowRight, Delete, ExpandLess, ExpandMore, Newspaper } from '@mui/icons-material'
import { DisplayDateFromString, DisplayTimeFromString } from '../../../services/DateTime'
import mainservice from '../../../services/MainService'
import InlineMenu from '../../elements/InlineMenu'
import GLItemStatusHistoryAdmin from './GLItemStatusHistoryAdmin'
import { GLItemDTO, GLItemKind, GLItemStatuses } from '../../../DTO/GLItemDTO'
import {
  formatMetaEuro,
  formatMetaPercent,
  hasStatusOptions,
  isMarketing,
} from './logChatMsgHelpers'

export default function LogChatMsgCompactRow({
  glitem,
  expanded,
  onToggleExpand,
  onClickDetail,
  onStatusChange,
  onNewsworthy,
  onDelete,
  canDelete,
}: {
  glitem: GLItemDTO
  expanded: boolean
  onToggleExpand: () => void
  onClickDetail?: (id: number) => void
  onStatusChange: (status: number) => Promise<void>
  onNewsworthy: () => Promise<void>
  onDelete: () => Promise<void>
  canDelete: boolean
}) {
  const dateSource = glitem.manualdate || glitem.created || ''
  const reminder = glitem.printReminderDate()

  return (
    <div className="LogChatMsg-compact">
      <Stack className="LogChatMsg-compactMeta" spacing={0.5}>
        {glitem.created && (
          <Box title={DisplayDateFromString(glitem.created || '')}>
            <Typography variant="body2" component="div">
              {DisplayDateFromString(dateSource)}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {DisplayTimeFromString(dateSource)}
            </Typography>
          </Box>
        )}
        {reminder && (
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ alignItems: 'center' }}
            title={`Erinnerung: ${reminder}`}
          >
            <Alarm
              fontSize="small"
              color={glitem.reminderExpired() ? 'error' : 'inherit'}
            />
            <Typography variant="caption">{reminder}</Typography>
          </Stack>
        )}
      </Stack>

      <Stack className="LogChatMsg-compactPeople" spacing={0}>
        {glitem.person && (
          <Button
            className="LogChatMsg-linkBtn"
            onClick={() => {
              mainservice.navTo([['view', 'spPersonEdit'], ['id', glitem.person?.id || -1]])
            }}
          >
            {glitem.person.getDisplayName()}
          </Button>
        )}
        {glitem.person?.customerName && (
          <Button
            className="LogChatMsg-linkBtn LogChatMsg-linkBtn--secondary"
            onClick={() => {
              mainservice.navTo([['view', 'spCustomerEdit'], ['id', glitem.person?.customerId || -1]])
            }}
          >
            {glitem.person.customerName}
          </Button>
        )}
      </Stack>

      <Box className="LogChatMsg-compactHeadline">
        <Typography
          variant="subtitle1"
          component="div"
          className="LogChatMsg-headlineText"
          title={glitem.headline || '(ohne Titel)'}
        >
          {glitem.headline || '(ohne Titel)'}
        </Typography>
        {isMarketing(glitem) && (glitem.target || glitem.nextstep) && (
          <Typography variant="caption" color="text.secondary" component="div" noWrap>
            {[glitem.target, glitem.nextstep].filter(Boolean).join(' · ')}
          </Typography>
        )}
      </Box>

      <Stack
        className="LogChatMsg-compactSide"
        direction="row"
        spacing={1}
        useFlexGap
        sx={{ alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}
      >
        {isMarketing(glitem) && (
          <Typography variant="body2" className="LogChatMsg-compactRevenue" component="span">
            {formatMetaEuro(glitem.revenue_potential)} € · {formatMetaPercent(glitem.revenue_probability)}
          </Typography>
        )}

        <Avatar
          title={glitem.assigned?.name || glitem.author?.name || ''}
          sx={{ bgcolor: glitem.getAssigneeColor(), width: 36, height: 36, fontSize: 14 }}
        >
          {glitem.getAssigneeInitials()}
        </Avatar>

        {hasStatusOptions(glitem) && (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <Select
              size="small"
              value={glitem.status}
              onChange={async (e) => {
                await onStatusChange(Number(e.target.value))
              }}
              aria-label="Status"
            >
              {GLItemStatuses[glitem.kind]
                .filter(s => s.status !== undefined)
                .map(s => (
                  <MenuItem
                    value={`${s.status}`}
                    key={`message-${glitem.id}-status-${s.status}`}
                  >
                    {s.name}
                  </MenuItem>
                ))}
            </Select>
            {glitem.kind === GLItemKind.marketing && glitem.id > -1 && (
              <GLItemStatusHistoryAdmin glItemId={glitem.id} kind={glitem.kind} />
            )}
          </Stack>
        )}

        {onClickDetail && glitem.id > -1 && (
          <Button
            variant="contained"
            size="small"
            endIcon={<ArrowRight />}
            onClick={() => onClickDetail(glitem.id)}
          >
            Details{glitem.childrenCount > -1 ? ` (${glitem.childrenCount})` : ''}
          </Button>
        )}

        {isMarketing(glitem) && glitem.id > -1 && (
          <IconButton
            title={glitem.newsworthy ? 'Newsworthy entfernen' : 'Als Newsworthy markieren'}
            color={glitem.newsworthy ? 'primary' : 'default'}
            onClick={onNewsworthy}
            sx={{ minWidth: 40, minHeight: 40 }}
          >
            <Newspaper />
          </IconButton>
        )}

        <IconButton
          onClick={onToggleExpand}
          title={expanded ? 'Zuklappen' : 'Aufklappen'}
          aria-expanded={expanded}
          sx={{ minWidth: 40, minHeight: 40 }}
        >
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>

        <InlineMenu
          title={<MoreVertIcon />}
          options={[
            {
              title: 'Löschen',
              icon: <Delete />,
              show: true,
              onClick: onDelete,
              disabled: !canDelete,
            },
          ]}
        />
      </Stack>
    </div>
  )
}
